/* Data, helpers, atoms. Exports to window for cross-script use. */

// ---------- helpers ----------
const cx = (...xs) => xs.filter(Boolean).join(' ');
const fmtMoney = (n, cur, dec) => n == null ? '—' : (cur === 'ARS' ? 'AR$ ' : 'U$S ') + n.toLocaleString('es-AR', dec != null ? { minimumFractionDigits: dec, maximumFractionDigits: dec } : undefined);
const fmtDate  = (d) => {
  const o = typeof d === 'string' ? new Date(d) : d;
  return o.toLocaleDateString('es-AR', { day:'2-digit', month:'short' }).replace('.','');
};
const fmtDateTime = (d) => {
  const o = typeof d === 'string' ? new Date(d) : d;
  return o.toLocaleString('es-AR', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit'});
};
const initialsOf = (name='') => name.split(' ').map(s=>s[0]).filter(Boolean).slice(0,2).join('').toUpperCase();
const authUrl = (path) => { const t = localStorage.getItem('crm_token'); return t ? `${path}?token=${encodeURIComponent(t)}` : path; };

// ---------- búsqueda ----------
// Una sola regla para todos los buscadores (MYS-0029): se busca por palabras sueltas, en cualquier
// orden, sin importar acentos, mayúsculas, puntos ni guiones. "haras sur" encuentra "HARAS DEL SUR",
// "sa" encuentra "S.A.", "30711150591" encuentra el CUIT "30-71115059-1". Antes cada buscador
// comparaba el texto literal, y el de Nueva solicitud/presupuesto era una lista que solo saltaba
// por las primeras letras.
const normBusqueda = (s) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLowerCase().replace(/\./g, '').replace(/[^a-z0-9@]+/g, ' ').trim();
function coincideBusqueda(consulta, ...campos) {
  const palabras = normBusqueda(consulta).split(' ').filter(Boolean);
  if (!palabras.length) return true;
  const texto = normBusqueda(campos.flat().filter(v => v != null && v !== '').join(' '));
  const junto = texto.replace(/ /g, '');
  // Las palabras con números se buscan también sin separadores (códigos y CUIT con guiones)
  return palabras.every(p => texto.includes(p) || (/\d/.test(p) && junto.includes(p)));
}

// ---------- Lucide icon wrapper ----------
function Icon({ name, size = 16, className = '', strokeWidth = 2 }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!ref.current || !window.lucide) return;
    ref.current.innerHTML = '';
    const svg = window.lucide.createElement(window.lucide.icons[toPascal(name)] || window.lucide.icons.Circle);
    svg.setAttribute('width',  size);
    svg.setAttribute('height', size);
    svg.setAttribute('stroke-width', strokeWidth);
    ref.current.appendChild(svg);
  }, [name, size, strokeWidth]);
  return <span ref={ref} className={cx('inline-flex shrink-0', className)} aria-hidden="true" />;
}
// Semáforo de seguimiento: marca manual del vendedor sobre qué chances hay de
// ganar una cotización. Pedido de Diego: verde es la que hay que ganar, y sin
// rojo — el rojo se lee como "problema", y una cotización difícil no es un
// problema, es una que no conviene perseguir. Por eso la tercera es gris.
// El amarillo es amarillo de verdad y no ámbar: el ámbar ya lo usa el borde de
// la tarjeta para el seguimiento vencido (⏰) y no se tienen que confundir.
// Sin marcar = todavía nadie la evaluó, que es distinto de "difícil de ganar".
const PRIORIDADES = [
  { id:'alta',  label:'Prioritaria',      desc:'Hay que ganarla sí o sí',          dot:'#16A34A', wash:'#F0FDF4', line:'#86EFAC' },
  { id:'media', label:'Se puede ganar',   desc:'Hay chances, pero no es urgente',  dot:'#EAB308', wash:'#FEFCE8', line:'#FDE047' },
  { id:'baja',  label:'Difícil de ganar', desc:'Ya se revisó, tiene pocas chances', dot:'#6B7280', wash:'#F3F4F6', line:'#D1D5DB' },
];
const prioridadDe = (id) => PRIORIDADES.find(p => p.id === id) || null;

// Regla de rechazo automático (los presupuestos "enviado" sin semáforo pasan solos a Rechazada a los N días).
// El backend manda `q.seguimiento30` con lo que dice la regla de ESE presupuesto (services/rechazoAutomatico.js);
// acá solo se traduce a texto. Es null cuando no aplica (no es un presupuesto enviado, o la regla está apagada).
function seguimientoDe(q) {
  const s = q && q.seguimiento30;
  if (!s) return null;
  // Recién le puso el semáforo: no esperar al próximo sync (25 s) para dejar de avisar
  if (s.estado === 'cuenta' && q.priority) return { ...s, estado: 'semaforo' };
  return s;
}
// { txt, aviso } para la ficha. `aviso` = quedan 7 días o menos: es lo único que merece color
function textoSeguimiento(s) {
  if (!s) return null;
  if (s.estado === 'cuenta') {
    if (s.faltan <= 0) return { txt: 'Se rechaza solo en la próxima corrida diaria', aviso: true };
    return { txt: `Se rechaza solo el ${fmtDate(s.rechazaEl)} (en ${s.faltan} ${s.faltan === 1 ? 'día' : 'días'})`, aviso: s.faltan <= 7 };
  }
  const motivo = {
    semaforo:   'lo seguís vos con el semáforo',
    pedido:     'ya hay un pedido',
    reabierto:  'se reabrió después de un rechazo automático',
  }[s.estado];
  return motivo ? { txt: `No se rechaza solo: ${motivo}`, aviso: false } : null;
}

function toPascal(k) {
  return k.split('-').map(s => s[0].toUpperCase() + s.slice(1)).join('');
}

// ---------- Logo ----------
function Logo({ size=28, tone='light' }) {
  // En fondo claro (login) envolvemos la imagen en un contenedor oscuro
  if (tone === 'dark') {
    return (
      <div style={{
        width: size, height: size,
        background: '#004669',
        borderRadius: Math.round(size * 0.22),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden',
        flexShrink: 0,
      }}>
        <img src="/Logo.png" alt="MySelec" style={{ width: size * 0.85, height: size * 0.85, objectFit: 'contain' }}/>
      </div>
    );
  }
  return (
    <img src="/Logo.png" alt="MySelec"
      style={{ width: size, height: size, objectFit: 'contain', flexShrink: 0 }}
    />
  );
}

// ---------- Avatar ----------
function Avatar({ name, size=24, tone, src }) {
  const palette = ['#004669','#156D98','#20759E','#16A76E','#7C5AC7','#E5930A','#0A5A82'];
  const idx = (name || '?').split('').reduce((a,c)=>a+c.charCodeAt(0),0) % palette.length;
  const bg = tone || palette[idx];
  if (src) {
    return (
      <img src={src} alt={name} title={name}
        className="rounded-full object-cover shrink-0"
        style={{ width:size, height:size, boxShadow: '0 0 0 1.5px rgba(255,255,255,0.8), 0 0 0 2.5px rgba(0,70,105,0.08)' }}
      />
    );
  }
  return (
    <span
      className="inline-flex items-center justify-center rounded-full text-white font-semibold leading-none shrink-0"
      style={{
        width:size, height:size, background:bg,
        fontSize: Math.max(10, size*0.40),
        letterSpacing: '0.02em',
        boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.12)',
      }}
      title={name}
    >
      {initialsOf(name)}
    </span>
  );
}

// ---------- Badge / Chip ----------
function Badge({ tone='gray', children, dot=false }) {
  const tones = {
    gray:   'bg-ink-300/30 text-ink-700',
    blue:   'badge-blue',
    navy:   'bg-navy-900 text-white',
    green:  'badge-green',
    amber:  'badge-amber',
    red:    'badge-red',
    purple: 'badge-purple',
    sky:    'bg-sky-50 text-sky-800',
    orange: 'bg-orange-50 text-orange-800',
    slate:  'badge-gray',
  };
  const dotColor = {
    gray:'#939598', blue:'#20759E', navy:'#004669', green:'#16A76E',
    amber:'#E5930A', red:'#D93636', purple:'#7C5AC7', sky:'#20759E', orange:'#E5760A', slate:'#939598'
  }[tone];
  return (
    <span className={cx('chip', tones[tone])}>
      {dot && <span className="w-1.5 h-1.5 rounded-full" style={{background:dotColor}}/>}
      {children}
    </span>
  );
}

// ---------- Domain data ----------
// Datos iniciales vacíos — se reemplazan con los datos reales de la API al iniciar sesión
const USERS   = [];
const CLIENTS = [];

// ---------- Cotizaciones ----------
const STAGES_F1 = [
  { id:'recibida',    label:'Solicitud Recibida',  tone:'gray'   },
  { id:'asignada',    label:'Asignada',            tone:'blue'   },
  { id:'armado',      label:'En Armado',           tone:'navy'   },
  { id:'proveedor',   label:'Esperando Proveedor', tone:'amber'  },
  { id:'oferta',      label:'Oferta Técnica',      tone:'sky'    },
  { id:'enviado',     label:'Presupuesto Enviado', tone:'orange' },
  { id:'aceptada',    label:'Aceptada',            tone:'green'  },
  { id:'rechazada',   label:'Rechazada',           tone:'red'    },
];

const QUOTES   = [];
const ACTIVITY = [];
const COMMENTS = {};

// Chart data — vacío hasta que la API responda
const CH_SELLERS    = [];
const CH_STAGE_DIST = [];
const CH_MONTHLY    = [];

// Expose to other scripts
Object.assign(window, {
  cx, fmtMoney, fmtDate, fmtDateTime, authUrl, normBusqueda, coincideBusqueda,
  PRIORIDADES, prioridadDe, seguimientoDe, textoSeguimiento,
  Icon, Logo, Avatar, Badge,
  USERS, CLIENTS, QUOTES,
  STAGES_F1, ACTIVITY, COMMENTS,
  CH_SELLERS, CH_STAGE_DIST, CH_MONTHLY,
});
